import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { ChannelServicePanel } from './ChannelServicePanel';
import { messageFor } from '../lib/errors';
import { connectChannel, updateChannel, type ChannelView } from './api';
import {
  PROVIDERS,
  connectableProviders,
  providerSpec,
  webhookUrl,
  type ProviderSpec,
} from './channel-providers';

/**
 * Connecting a channel, and changing one (context.md §45–§56). Phase 59.
 *
 * This is the screen an omnichannel product is judged on: every tenant connects their own accounts,
 * with their own credentials, and nothing about one tenant's WhatsApp is shared with another's. The
 * fields come from `channel-providers.ts` so the form is the same shape for every provider and
 * adding one is data rather than a branch.
 *
 * Three things it does that a naive form would not:
 *
 * **A secret already set shows as set, not as empty.** An empty password box means "I did not
 * retype it", and the server keeps what it has. The alternative is an edit that silently wipes a
 * token — the exact failure that makes people stop editing channels.
 *
 * **The webhook URL and the verify token are shown after saving.** Half of connecting WhatsApp
 * happens in Meta's dashboard, and a tenant who cannot see the URL cannot finish. The verify token
 * is returned exactly once by the API, so this is the only moment it can ever be displayed.
 *
 * **Nothing sealed is ever fetched back.** The form knows *which* credentials exist, never what
 * they are (context.md §138).
 */

export function ChannelDialog({
  channel,
  onClose,
}: {
  /** An existing channel to edit, or null to connect a new one. */
  channel: ChannelView | null;
  onClose: () => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const client = useQueryClient();

  const [provider, setProvider] = useState(
    channel?.provider ?? connectableProviders()[0]!.provider,
  );
  const [name, setName] = useState(channel?.name ?? '');
  const [values, setValues] = useState<Record<string, string>>(() => initialValues(channel));
  const [isDefault, setIsDefault] = useState(channel?.isDefault ?? false);
  const [error, setError] = useState<string | null>(null);

  /** Set once, after connecting: the one moment the verify token can be shown. */
  const [connected, setConnected] = useState<{ channel: ChannelView; secret: string } | null>(null);

  const spec = providerSpec(provider) ?? PROVIDERS[0]!;

  useEffect(() => {
    const element = dialog.current;

    if (element && !element.open) element.showModal();
  }, []);

  const save = useMutation({
    mutationFn: async () => {
      const { channelFields, config } = split(spec, values);

      if (channel) {
        await updateChannel(channel.id, { name: name.trim(), ...channelFields, config, isDefault });

        return null;
      }

      return connectChannel({
        kind: spec.kind,
        provider: spec.provider,
        name: name.trim(),
        ...channelFields,
        config,
        isDefault,
      });
    },
    onSuccess: (created) => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['channels'] });

      // A new channel stays open on the instructions; an edit is done.
      if (created) setConnected({ channel: created, secret: created.webhookSecret });
      else onClose();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const missing = spec.fields.some(
    (field) =>
      field.required &&
      (values[field.key] ?? '').trim() === '' &&
      !(field.secret && channel?.secretsSet.includes(field.key)),
  );

  return (
    <dialog
      ref={dialog}
      className="confirm confirm--wide"
      aria-label={channel ? t('channels.edit') : t('channels.connect')}
      onCancel={(cancelEvent) => {
        cancelEvent.preventDefault();
        onClose();
      }}
      onClose={onClose}
    >
      {connected ? (
        <Instructions
          spec={spec}
          channel={connected.channel}
          verifyToken={connected.secret}
          onClose={onClose}
        />
      ) : (
        <form
          onSubmit={(submitEvent) => {
            submitEvent.preventDefault();

            if (name.trim() && !missing) save.mutate();
          }}
        >
          <h2>{channel ? t('channels.edit') : t('channels.connect')}</h2>

          {/* The provider decides the whole form, so it cannot change under an existing channel:
              the credentials would no longer mean anything. */}
          <label className="stack__field">
            <span>{t('channels.providerChoice')}</span>
            <select
              className="select"
              value={provider}
              disabled={channel !== null}
              onChange={(changeEvent) => {
                setProvider(changeEvent.target.value);
                setValues({});
              }}
            >
              {connectableProviders().map((entry) => (
                <option key={entry.provider} value={entry.provider}>
                  {t(entry.labelKey)}
                </option>
              ))}
            </select>
          </label>

          <p className="muted small">{t(spec.descriptionKey)}</p>

          <label className="stack__field">
            <span>
              {t('channels.nameField')} <span className="required">*</span>
            </span>
            <input
              value={name}
              maxLength={120}
              autoFocus
              placeholder={t('channels.namePlaceholder')}
              onChange={(changeEvent) => setName(changeEvent.target.value)}
            />
          </label>

          {spec.fields.map((field) => {
            const alreadySet = field.secret && (channel?.secretsSet.includes(field.key) ?? false);

            return (
              <label key={field.key} className="stack__field">
                <span>
                  {t(field.labelKey)}
                  {field.required && !alreadySet && <span className="required"> *</span>}
                </span>

                <input
                  type={field.secret ? 'password' : 'text'}
                  value={values[field.key] ?? ''}
                  autoComplete={field.secret ? 'new-password' : 'off'}
                  placeholder={alreadySet ? t('channels.secretKept') : (field.placeholder ?? '')}
                  onChange={(changeEvent) =>
                    setValues({ ...values, [field.key]: changeEvent.target.value })
                  }
                />

                {field.hintKey && <span className="muted small">{t(field.hintKey)}</span>}
              </label>
            );
          })}

          <label className="field field--inline">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(changeEvent) => setIsDefault(changeEvent.target.checked)}
            />
            <span>{t('channels.makeDefault')}</span>
          </label>

          {error && (
            <p className="alert" role="alert">
              {error}
            </p>
          )}

          <div className="confirm__actions">
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={onClose}
            >
              {t('confirm.cancel')}
            </button>

            <button
              type="submit"
              className="button button--primary button--compact"
              disabled={name.trim() === '' || missing || save.isPending}
            >
              {save.isPending ? t('work.creating') : t('channels.save')}
            </button>
          </div>
        </form>
      )}

      {/*
       * Opening hours, only on a channel that exists (Phase 139).
       *
       * It saves on its own endpoint rather than as part of the form above: the credentials and
       * the working week are two different decisions, made by different people at different
       * times, and one Save for both would mean retyping a password to change a closing time.
       */}
      {channel && (
        <section className="stack">
          <h3>{t('channels.service')}</h3>
          <ChannelServicePanel channelId={channel.id} />
        </section>
      )}
    </dialog>
  );
}

/**
 * What to do in the provider's own dashboard.
 *
 * Shown once, because the verify token is returned once. A tenant who closes this without copying
 * it has to reconnect the channel — which is said plainly rather than discovered.
 */
function Instructions({
  spec,
  channel,
  verifyToken,
  onClose,
}: {
  spec: ProviderSpec;
  channel: ChannelView;
  verifyToken: string;
  onClose: () => void;
}): React.JSX.Element {
  const url = webhookUrl(spec, channel.id);

  return (
    <div className="stack">
      <h2>{t('channels.connected')}</h2>

      {url ? (
        <>
          <p className="muted">{t('channels.finishAt').replace('{provider}', t(spec.labelKey))}</p>

          <label className="stack__field">
            <span>{t('channels.webhookUrl')}</span>
            <input value={url} readOnly onFocus={(event) => event.target.select()} />
            <span className="muted small">{t('channels.webhookUrl.hint')}</span>
          </label>

          <label className="stack__field">
            <span>{t('channels.verifyToken')}</span>
            <input value={verifyToken} readOnly onFocus={(event) => event.target.select()} />
            <span className="muted small">{t('channels.verifyToken.once')}</span>
          </label>

          <p className="notice notice--warning">
            <span>{t('channels.subscribeMessages')}</span>
          </p>
        </>
      ) : (
        <p className="muted">{t('channels.connectedNoWebhook')}</p>
      )}

      <div className="confirm__actions">
        <button type="button" className="button button--primary button--compact" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </div>
  );
}

/** The fields a form starts with: everything non-secret that is already on the channel. */
function initialValues(channel: ChannelView | null): Record<string, string> {
  if (!channel) return {};

  const values: Record<string, string> = {};

  if (channel.address) values.address = channel.address;
  if (channel.externalId) values.externalId = channel.externalId;

  for (const [key, value] of Object.entries(channel.config ?? {})) {
    if (typeof value === 'string') values[key] = value;
  }

  return values;
}

/**
 * Splits what the form collected into the channel's own columns and its provider config.
 *
 * A blank secret is dropped here as well as on the server. Sending it would be sending "" for a
 * credential, and relying on one side of a boundary to be careful is how the other side stops
 * being.
 */
function split(
  spec: ProviderSpec,
  values: Record<string, string>,
): { channelFields: Record<string, string | null>; config: Record<string, unknown> } {
  const channelFields: Record<string, string | null> = {};
  const config: Record<string, unknown> = {};

  for (const field of spec.fields) {
    const value = (values[field.key] ?? '').trim();

    if (field.on === 'channel') {
      channelFields[field.key] = value || null;
      continue;
    }

    if (field.secret && value === '') continue;

    config[field.key] = value;
  }

  return { channelFields, config };
}
